/**
 * Answering a game night.
 *
 * "Going" on its own could not tell a host the difference between six people
 * who had not looked at the event yet and six who had looked and cannot come.
 * Three answers, one of which is honest about not knowing, and the counts
 * beside them so the host can see the shape of the evening at a glance.
 */

import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { Icon } from '@/icons/Icon';
import type { CrewEvent } from './Crew';
import { useToast } from './ui';

export type RsvpAnswer = 'going' | 'maybe' | 'cant';

const ANSWERS: { id: RsvpAnswer; label: string; short: string; icon: string; tone: string }[] = [
  { id: 'going', label: 'Going', short: 'Going', icon: 'check', tone: 'var(--good)' },
  { id: 'maybe', label: 'Maybe', short: 'Maybe', icon: 'hourglass', tone: 'var(--warn)' },
  { id: 'cant', label: "Can't make it", short: "Can't", icon: 'close', tone: 'var(--bad)' }
];

export function Rsvp({ crewId, event, compact, onDone }: {
  crewId: string;
  event: CrewEvent;
  /** Icons only, for a row that is already busy. */
  compact?: boolean;
  onDone?: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  /* Answered here a moment ago, so the buttons move before the refetch. */
  const [local, setLocal] = useState<RsvpAnswer | null | undefined>(undefined);
  const mine = local !== undefined ? local : (event.myAnswer ?? (event.imGoing ? 'going' : null));

  async function answer(id: RsvpAnswer) {
    if (busy) return;
    const next = mine === id ? null : id;
    setBusy(true);
    setLocal(next);
    try {
      await api.post('/api/crews/' + crewId + '/events/' + event.id + '/rsvp', { answer: next ?? 'none', going: next === 'going' });
      void queryClient.invalidateQueries({ queryKey: ['crew'] });
      void queryClient.invalidateQueries({ queryKey: ['crews'] });
      toast(next === 'going' ? 'See you there' : next === 'maybe' ? 'Marked as maybe' : next === 'cant' ? 'The host knows you cannot make it' : 'Answer cleared', 'good');
      onDone?.();
    } catch (err) {
      setLocal(undefined);
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  const counts: Record<RsvpAnswer, number> = {
    going: event.goingCount ?? 0,
    maybe: event.maybeCount ?? 0,
    cant: event.cantCount ?? 0
  };

  return (
    <div role="group" aria-label="Are you going?" className="flex flex-none items-center gap-1">
      {ANSWERS.map((a) => {
        const on = mine === a.id;
        const n = counts[a.id] + (on && mine !== (event.myAnswer ?? null) ? 1 : 0);
        return (
          <button
            key={a.id}
            type="button"
            disabled={busy}
            aria-pressed={on}
            title={a.label}
            onClick={() => void answer(a.id)}
            className={cn(
              'flex h-9 items-center gap-1.5 rounded-[calc(10px*var(--radius-mul,1))] text-[12.5px] font-bold outline-none',
              compact ? 'px-2.5' : 'px-3',
              'transition-[background-color,color,box-shadow] duration-[160ms] focus-visible:shadow-[0_0_0_2px_var(--accent)] disabled:opacity-50',
              on ? 'text-[var(--bg-0)]' : 'bg-surface-2 text-dim hover:bg-surface-3 hover:text-ink'
            )}
            style={on ? { background: a.tone } : undefined}
          >
            <Icon name={a.icon} size={13} />
            {compact ? null : <span>{a.short}</span>}
            <span
              aria-hidden={!n}
              className="text-center tabular-nums opacity-80"
              /* Two digits' worth, so 9 to 10 does not move anything either. */
              style={{ minWidth: '1.6ch' }}
            >
              {n || ''}
            </span>
          </button>
        );
      })}
    </div>
  );
}
